import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OpportunityService } from '../OpportunityService';
import { JobService } from '../JobService';

// Direct CSE → TA sourcing — replaces the old "route to TA Lead" step.
export const useAssignToTa = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ opportunityId, data }) =>
      OpportunityService.assignToTa(opportunityId, data),
    onSuccess: (_, { opportunityId }) => {
      // Invalidate all opportunities queries (cse, ta-lead, ta)
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      // Invalidate specific opportunity details
      queryClient.invalidateQueries({
        queryKey: ['opportunity', 'details', opportunityId],
      });
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['opportunity'] });
      queryClient.invalidateQueries({ queryKey: ['ta', 'summary'] });
      queryClient.invalidateQueries({ queryKey: ['cse', 'dashboard'] });
      // Invalidate candidates as routing may affect candidate lists
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
    },
  });
};

// CSE claims an unassigned ("New" tab) requirement for themselves — was
// dead code (defined, never wired to any mutation hook or UI) until this
// button; see OpportunityService.claimOpportunity's own comment for the
// endpoint's shape.
export const useClaimOpportunity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (opportunityId) =>
      OpportunityService.claimOpportunity(opportunityId),
    onSuccess: (_, opportunityId) => {
      // Same invalidation set useAssignToTa uses above — a claim moves the
      // requirement off the New tab (current_cse_id/status changes), so
      // every list/summary that could show it needs a refetch.
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      queryClient.invalidateQueries({
        queryKey: ['opportunity', 'details', opportunityId],
      });
      queryClient.invalidateQueries({ queryKey: ['opportunity'] });
      // Prefix match catches both ['cse','dashboard',...] (metrics,
      // high-priority) and ['cse','requirements','summary'] (this page's
      // own tab counts) rather than naming each one.
      queryClient.invalidateQueries({ queryKey: ['cse'] });
    },
  });
};

export const useAssignPartnerToAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, data }) =>
      OpportunityService.assignPartnerToAssignment(assignmentId, data),
    onSuccess: (_, { assignmentId }) => {
      queryClient.invalidateQueries({
        queryKey: ['ta-assignment', 'distribution', assignmentId],
      });
    },
  });
};

export const usePublishAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, data }) =>
      OpportunityService.publishAssignment(assignmentId, data),
    onSuccess: (_, { assignmentId }) => {
      queryClient.invalidateQueries({ queryKey: ['opportunity'] });
      queryClient.invalidateQueries({
        queryKey: ['ta-assignment', 'distribution', assignmentId],
      });
    },
  });
};

export const useAssignInstitutionToAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, data }) =>
      OpportunityService.assignInstitutionToAssignment(assignmentId, data),
    onSuccess: (_, { assignmentId }) => {
      queryClient.invalidateQueries({
        queryKey: ['assignment-institutions', assignmentId],
      });
    },
  });
};

export const useAssignInstitutionToJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobCode, data }) =>
      JobService.assignInstitutionToJob(jobCode, data),
    onSuccess: (_, { jobCode }) => {
      queryClient.invalidateQueries({
        queryKey: ['job-institutions', jobCode],
      });
    },
  });
};

export const useDeleteTaAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ parentAssignmentId, taId }) =>
      OpportunityService.deleteTaAssignment(parentAssignmentId, taId),
    onSuccess: (_, { parentAssignmentId }) => {
      queryClient.invalidateQueries({
        queryKey: ['ta-assignment', 'distribution', parentAssignmentId],
      });
    },
  });
};
